import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiComparisonService, ComparisonResult, ComparisonStats } from '../../_services/api-comparison.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-api-comparison-panel',
  templateUrl: './api-comparison-panel.component.html',
  styleUrls: ['./api-comparison-panel.component.scss']
})
export class ApiComparisonPanelComponent implements OnInit, OnDestroy {
  results: ComparisonResult[] = [];
  stats: ComparisonStats = { total: 0, passed: 0, failed: 0 };
  isMinimized = true;
  filter: 'all' | 'ok' | 'ko' = 'all';
  expandedResultId: string | null = null;
  showPayload: 'node' | 'php' | null = null;

  // URLs de base pour affichage
  nodeBaseUrl = environment.apiUrl;
  phpBaseUrl = environment.phpApiUrl;

  private subscription: Subscription;

  constructor(private apiComparisonService: ApiComparisonService) {}

  ngOnInit(): void {
    this.subscription = this.apiComparisonService.results$.subscribe(results => {
      this.results = results;
      this.stats = this.apiComparisonService.getStats();
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get filteredResults(): ComparisonResult[] {
    switch (this.filter) {
      case 'ok':
        return this.results.filter(r => r.isMatch);
      case 'ko':
        return this.results.filter(r => !r.isMatch);
      default:
        return this.results;
    }
  }

  toggleMinimized(): void {
    this.isMinimized = !this.isMinimized;
  }

  setFilter(filter: 'all' | 'ok' | 'ko'): void {
    this.filter = filter;
  }

  toggleExpand(resultId: string): void {
    this.expandedResultId = this.expandedResultId === resultId ? null : resultId;
  }

  isExpanded(resultId: string): boolean {
    return this.expandedResultId === resultId;
  }

  clearResults(): void {
    this.apiComparisonService.clearResults();
  }

  formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  }

  formatTimestamp(date: Date): string {
    return new Date(date).toLocaleTimeString('fr-FR');
  }

  getStatusClass(result: ComparisonResult): string {
    return result.isMatch ? 'status-ok' : 'status-ko';
  }

  getMethodClass(method: string): string {
    return `method-${method.toLowerCase()}`;
  }

  /**
   * Retourne l'URL complète pour Node.js
   */
  getNodeUrl(result: ComparisonResult): string {
    return `${this.nodeBaseUrl}${result.endpoint}`;
  }

  /**
   * Retourne l'URL complète pour PHP
   */
  getPhpUrl(result: ComparisonResult): string {
    return `${this.phpBaseUrl}${result.endpoint}`;
  }

  /**
   * Formate un objet JSON pour affichage
   */
  formatJson(data: any): string {
    if (!data) return 'null';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }

  /**
   * Toggle l'affichage du payload
   */
  togglePayload(type: 'node' | 'php'): void {
    this.showPayload = this.showPayload === type ? null : type;
  }

  /**
   * Copie le texte dans le presse-papier
   */
  async copyToClipboard(text: string, event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback pour les anciens navigateurs
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  /**
   * Copie l'URL complète dans le presse-papier
   */
  copyUrl(result: ComparisonResult, api: 'node' | 'php', event: Event): void {
    const url = api === 'node' ? this.getNodeUrl(result) : this.getPhpUrl(result);
    this.copyToClipboard(url, event);
  }

  /**
   * Copie le payload dans le presse-papier
   */
  copyPayload(result: ComparisonResult, api: 'node' | 'php', event: Event): void {
    const payload = api === 'node' ? result.nodeResponse : result.phpResponse;
    this.copyToClipboard(this.formatJson(payload), event);
  }
}
